<script setup lang="ts">
import type { Components } from '@/api/generated/api';
import ChevronRightIcon from '@/assets/icons/ChevronRightIcon.vue';
import { SearchHighlightTextInjectionKey } from '@/composables/useSearchSearchHighlightText';
import { useSchoolSubject } from '@/composables/schoolSubject/useSchoolSubject';
import { useSchoolSubjectGroup } from '@/composables/schoolSubjectGroups/useSchoolSubjectGroup';
import { useMobile } from '@/composables/useMobile';
import CompetenceDomainIcon from '@/core/competences/CompetenceDomainIcon.vue';
import PositionablePopover from '@/core/dialogs/PositionablePopover.vue';
import EmptyPlaceholder from '@/core/elements/EmptyPlaceholder.vue';
import SearchTextHighlighter from '@/core/elements/SearchTextHighlighter.vue';
import SelectionPopoverDivider from '@/core/selects/SelectionPopoverDivider.vue';
import SelectionPopoverOptionRow from '@/core/selects/SelectionPopoverOptionRow.vue';
import SelectionPopoverSearchHeader from '@/core/selects/SelectionPopoverSearchHeader.vue';
import { focusSelectionSearchOnOpen } from '@/core/selects/selectionPopover.utils';
import { groupCompetenceDomainsBySchoolSubject } from '@/utils/competence.domain.utils';
import { matchesAllSearchWords } from 'common/utils/string.utils';
import { computed, provide, readonly, ref, useTemplateRef } from 'vue';

defineOptions({ name: 'CompetenceSelectionPopover' });

const emit = defineEmits<{
  'add:competence': [competenceId: number];
  'remove:competence': [competenceId: number];
  'update:competences': [competenceIds: number[]];
  hide: [];
}>();

const props = withDefaults(
  defineProps<{
    anchorElement: HTMLElement | null;
    availableCompetenceDomains: Components.Schemas.CompetenceDomainDto[];
    allSchoolSubjects: Components.Schemas.SchoolSubjectDto[];
    commitOnClose?: boolean;
    disableClickOutside?: boolean;
    disabled?: boolean;
    restrictedCompetenceIds?: number[];
    selectedCompetences: Components.Schemas.CompetenceDto[];
    hideSchoolSubjects: boolean;
    mobileDrawer?: boolean;
  }>(),
  {
    commitOnClose: false,
    disableClickOutside: false,
    disabled: false,
    mobileDrawer: false,
    restrictedCompetenceIds: () => [],
  },
);

const { getSchoolSubjectColor } = useSchoolSubject();
const { allSchoolSubjectGroups } = useSchoolSubjectGroup();
const { isMobile } = useMobile();

const popoverRef = useTemplateRef('popoverRef');
const searchHeaderRef = useTemplateRef('searchHeaderRef');
const searchText = ref('');
const expandedDomainIds = ref<Set<number>>(new Set());
provide(SearchHighlightTextInjectionKey, readonly(searchText));

const showAll = ref(false);
const restrictedIdSet = computed<Set<number>>(() => new Set(props.restrictedCompetenceIds));
const hasRestriction = computed<boolean>(() => restrictedIdSet.value.size > 0);
const isRestricted = computed<boolean>(() => hasRestriction.value && !showAll.value);
const effectiveCompetenceDomains = computed<Components.Schemas.CompetenceDomainDto[]>(() => {
  if (!isRestricted.value) return props.availableCompetenceDomains;
  return props.availableCompetenceDomains
    .map((domain) => ({
      ...domain,
      competences: domain.competences.filter((competence) => restrictedIdSet.value.has(competence.id)),
    }))
    .filter((domain) => domain.competences.length > 0);
});
const showAllToggleVisible = computed<boolean>(() => {
  if (!hasRestriction.value || showAll.value) return false;
  const total = props.availableCompetenceDomains.reduce((sum, domain) => sum + domain.competences.length, 0);
  const shown = props.availableCompetenceDomains.reduce(
    (sum, domain) => sum + domain.competences.filter((competence) => restrictedIdSet.value.has(competence.id)).length,
    0,
  );
  return shown < total;
});

const filteredCompetenceDomains = computed<Components.Schemas.CompetenceDomainDto[]>(() => {
  const query = searchText.value.trim();

  return effectiveCompetenceDomains.value
    .map((domain) => {
      const domainMatches = query ? matchesAllSearchWords([domain.name], query) : false;
      const competences =
        !query || domainMatches
          ? domain.competences
          : domain.competences.filter((competence) => matchesAllSearchWords([competence.description], query));
      return { ...domain, competences };
    })
    .filter((domain) => domain.competences.length > 0);
});
const domainGroups = computed(() =>
  groupCompetenceDomainsBySchoolSubject(
    filteredCompetenceDomains.value,
    props.allSchoolSubjects,
    allSchoolSubjectGroups.value,
  ),
);
const selectedCompetenceIds = computed(() => new Set(props.selectedCompetences.map((competence) => competence.id)));

const draftIds = ref<number[]>([]);
// only commit if the popover was actually opened — guards against spurious hide events (e.g. initial mount)
let commitPending = false;

const isExpanded = (domainId: number): boolean => !!searchText.value.trim() || expandedDomainIds.value.has(domainId);

const toggleDomain = (domainId: number): void => {
  const next = new Set(expandedDomainIds.value);
  if (next.has(domainId)) next.delete(domainId);
  else next.add(domainId);
  expandedDomainIds.value = next;
};

const isSelected = (competence: Components.Schemas.CompetenceDto): boolean =>
  props.commitOnClose ? draftIds.value.includes(competence.id) : selectedCompetenceIds.value.has(competence.id);

const toggleCompetence = (competence: Components.Schemas.CompetenceDto): void => {
  if (props.disabled) return;
  if (props.commitOnClose) {
    draftIds.value = isSelected(competence)
      ? draftIds.value.filter((id) => id !== competence.id)
      : [...draftIds.value, competence.id];
    return;
  }
  if (isSelected(competence)) emit('remove:competence', competence.id);
  else emit('add:competence', competence.id);
};

const show = async (): Promise<void> => {
  searchText.value = '';
  showAll.value = false;
  // seed the draft from the current selection each time the popover opens
  if (props.commitOnClose) {
    draftIds.value = props.selectedCompetences.map((competence) => competence.id);
    commitPending = true;
  }
  const firstDomainId = domainGroups.value.at(0)?.competenceDomains.at(0)?.id;
  expandedDomainIds.value = new Set(firstDomainId != null ? [firstDomainId] : []);
  await popoverRef.value?.show();
  await focusSelectionSearchOnOpen(searchHeaderRef.value, isMobile.value);
};

const hide = (): void => popoverRef.value?.hide();

const handleHide = (): void => {
  if (props.commitOnClose && commitPending) {
    commitPending = false;
    emit('update:competences', draftIds.value);
  }
  emit('hide');
};

defineExpose({ show, hide, isOpen: computed(() => !!popoverRef.value?.isOpen) });
</script>

<template>
  <PositionablePopover
    ref="popoverRef"
    scroll-mode="content"
    :anchor-element="props.anchorElement"
    :disable-click-outside="props.disableClickOutside"
    flip-strategy="both"
    :mobile-drawer-label="$t('competence.select')"
    :mobile-drawer-mode="props.mobileDrawer"
    :mobile-viewport-mode="!props.mobileDrawer"
    class="w-selection-popover"
    :offset-y="10"
    lazy-content
    @hide="handleHide"
  >
    <template #default="{ mobileDrawer: isMobileDrawer }">
      <div class="flex min-h-0 min-w-0 w-full flex-1 flex-col" :class="!isMobileDrawer && 'max-h-selection-popover'">
        <SelectionPopoverSearchHeader
          ref="searchHeaderRef"
          v-model="searchText"
          :placeholder="$t('competence.searchPlaceholder')"
        />

        <div
          class="flex min-h-0 min-w-0 flex-1 flex-col gap-px overflow-x-hidden overflow-y-auto overscroll-contain px-smd pb-smd"
        >
          <template
            v-for="(subjectGroup, subjectIndex) in domainGroups"
            :key="subjectGroup.schoolSubject?.id ?? 'subjectless'"
          >
            <template v-for="(domain, domainIndex) in subjectGroup.competenceDomains" :key="domain.id">
              <SelectionPopoverDivider v-if="subjectIndex > 0 || domainIndex > 0" />
              <span
                v-if="domainIndex === 0 && subjectGroup.schoolSubject && !props.hideSchoolSubjects"
                class="px-sm pt-sm text-footer-semibold text-secondary"
              >
                {{ subjectGroup.schoolSubject.name }}
              </span>

              <button
                type="button"
                class="flex min-h-11 w-full items-center gap-sm rounded-md p-sm text-left state-effects"
                :aria-expanded="isExpanded(domain.id)"
                @click="toggleDomain(domain.id)"
              >
                <ChevronRightIcon
                  class="size-icon-xs shrink-0 text-secondary transition-transform duration-200"
                  :class="isExpanded(domain.id) && 'rotate-90'"
                />
                <CompetenceDomainIcon
                  v-if="domain.schoolSubjectId"
                  :color="getSchoolSubjectColor(domain.schoolSubjectId)"
                  :icon-name="domain.symbol"
                  size="sm"
                />
                <span
                  class="highlight-offset min-w-0 flex-1 truncate text-body font-semibold text-secondary"
                  :title="domain.name"
                >
                  <SearchTextHighlighter :text="domain.name" />
                </span>
              </button>

              <template v-if="isExpanded(domain.id)">
                <SelectionPopoverOptionRow
                  v-for="competence in domain.competences"
                  :key="competence.id"
                  class="px-md"
                  :disabled="props.disabled"
                  highlight-offset
                  indicator="checkbox"
                  :selected="isSelected(competence)"
                  :title="competence.description"
                  @select="toggleCompetence(competence)"
                >
                  <SearchTextHighlighter :text="competence.description" />
                </SelectionPopoverOptionRow>
              </template>
            </template>
          </template>
          <EmptyPlaceholder :data="domainGroups" :message="$t('general.noResults')" />
        </div>

        <div v-if="showAllToggleVisible || $slots.footer" class="shrink-0 px-smd pt-xs pb-smd">
          <button
            v-if="showAllToggleVisible"
            type="button"
            class="flex w-full items-center rounded-md p-sm text-left text-body text-secondary underline underline-offset-2"
            @click="showAll = true"
          >
            {{ $t('competence.showAll') }}
          </button>
          <slot v-else name="footer" />
        </div>
      </div>
    </template>
  </PositionablePopover>
</template>
